import { Spacing, Button, TextInput, Icon } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useStore, User, Draft, UserInviteData, useUserMutation } from 'zens-store'
import { Link, useNumberParams, usePaginationParam } from 'zens-router'
import { LayoutHeader, UserListHeader, UserTable } from '@dms/component'
import { useBreadcrumb } from '@dms/hook'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
  },
  header: {
    flexDirection: 'row',
  },
  search: {
    minWidth: 300,
    marginRight: Spacing.S,
  },
})

const UserList = () => {
  const [search, setSearch] = useState('')
  const { message } = useStore()
  const { t } = useTranslation()
  const [{ companyId }] = useNumberParams()
  const [pagination, setPagination] = usePaginationParam()
  const mutation = useUserMutation({ companyId })

  const breadcrumb = useBreadcrumb([
    {
      title: t('Users'),
    },
  ])

  function updateSearch(search: string) {
    setSearch(search)
    setPagination({ page: 0, size: pagination.size })
  }

  const resendInvite = async (user: User) => {
    try {
      const draft = new Draft<UserInviteData>({ id: user.id })
      await mutation.reinvite(draft)
      message.add({
        message: t('Invitation was resent successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`USER: Invitation failed: ${errorMessage}`)
      message.add({
        message: `${t('Invitation could not be sent')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }

  const renderHeader = () => {
    return (
      <View style={styles.header}>
        <TextInput
          left="search"
          placeholder={t('Search name or email')}
          right={
            search ? (
              <TouchableOpacity onPress={() => updateSearch('')} testID="UserList.Search.Clear">
                <Icon name="close" size="S" />
              </TouchableOpacity>
            ) : undefined
          }
          value={search}
          onChangeText={updateSearch}
          style={styles.search}
          testID="UserList.Search"
        />
        <Link href={`/companies/${companyId}/users/invite`} asChild>
          <Button title={t('Invite new user')} left="plus" testID="UserList.Invite" />
        </Link>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <Suspense
        fallback={
          <LayoutHeader.Skeleton
            breadcrumb
            title
            accessory={
              <View style={styles.header}>
                <TextInput.Skeleton style={styles.search} />
                <Button.Skeleton />
              </View>
            }
          />
        }
      >
        <Suspense fallback={<UserListHeader.Skeleton />}>
          <UserListHeader breadcrumb={breadcrumb} companyId={companyId} search={search} accessory={renderHeader()} />
        </Suspense>
      </Suspense>
      <Suspense fallback={<UserTable.Skeleton style={styles.content} />}>
        <UserTable
          companyId={companyId}
          search={search}
          style={styles.content}
          pagination={pagination}
          onPaginationChange={setPagination}
          onResendInvite={resendInvite}
        />
      </Suspense>
    </View>
  )
}

export default UserList
